<template>
    <div>
        <notification-modal v-model:is-open="showReportModal" :is-error-modal="reportModalError">
            <template #title-content>
                {{ modalTitle }}
            </template>

            {{ modalMessage }}
        </notification-modal>
        <base-report>
            <h3 class="text-left text-lg font-semibold">Detailed Alarm Report</h3>
            <template #left-column>
                <div class="mb-4 lg:mb-12">
                    <p>
                        Get a detailed list of alarms and actions taken on them for the provided points.
                        The report will look back in time for the same amount of time as the automatic sending interval.
                        ({{ intervals[interval] }})
                    </p>
                </div>

                <div class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                    <label for="month">{{ interval === 'monthly' ? 'Month' : 'End of Interval' }}</label>
                    <date-time-picker   id="month"
                                        v-model="month"
                                        :format="format"
                                        :view-mode="viewMode"
                                        :min-date="minDate"
                                        :max-date="maxDate"
                    ></date-time-picker>
                </div>

                <!-- which half month to report on during semimonths -->
                <div    v-if="interval === 'semi-monthly'"
                        class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                    <span>Half of Month Reported on</span>
                    <select2 :options="semiMonthIntervals"
                             v-model="semiMonthSection"
                             :close-on-select="true"
                    ></select2>
                </div>

                <div class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                    <label for="selected_meters[]">Select One or More Points</label>
                    <select2    id="selected_meters[]"
                                :multiple="true"
                                :options="meters"
                                v-model="selectedMeters"
                                :allow-clear="true"
                    ></select2>
                </div>

                <div class="flex justify-center">
                    <button v-if="isRunning"
                            class="col-span-2 w-full lg:w-2/3 my-4 relative inline-flex items-center justify-center
                            px-4 py-2 border border-transparent text-sm bg-blue-600 text-white font-medium py-2 px-4
                            rounded opacity-50 cursor-not-allowed">
                        <span class="lnr icon-loading animate-spin mr-3"></span>
                        Running Report, Please Wait
                    </button>
                    <button v-else-if="selectedMeters.length === 0"
                            class="col-span-2 w-full lg:w-2/3 my-4 relative inline-flex items-center justify-center
                            px-4 py-2 border border-transparent text-sm bg-blue-600 text-white font-medium py-2 px-4
                            rounded opacity-50 cursor-not-allowed">
                        Please Select At Least One Meter
                    </button>
                    <button v-else
                            class="col-span-2 w-full lg:w-2/3 my-4 relative inline-flex items-center justify-center px-4 py-2 border
                            border-transparent text-sm leading-5 font-medium rounded-md text-white bg-blue-600
                            hover:bg-blue-500 focus:outline-none focus:ring-blue-200 focus:border-blue-700
                            active:bg-blue-700"
                            @click="runReport">
                        Run Report
                    </button>
                </div>
            </template>

            <template #right-column>
                <h4 class="text-center">Additional Settings</h4>

                <!-- Seperate Minimum & Maximum -->
                <div class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">

                </div>


                <div class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                    <span></span>
                    <!-- Input Method -->
                </div>

                <h4 class="text-center">Persistent Settings</h4>

                <!-- Automatic Sending Interval -->
                <div class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                    <span>Automatic Sending Interval</span>
                    <select2 :options="intervals"
                             v-model="interval"
                             :close-on-select="true"
                    ></select2>
                </div>

                <!-- Day of week to send -->
                <div    v-if="permissions.can('manage_detailed_alarm_report')
                              && interval === 'weekly'"
                        class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                    <span>Day of Week to Send Report</span>
                    <select2 :options="weekdayOptions"
                             v-model="receiveWeekday"
                             :close-on-select="true"
                    ></select2>
                </div>

                <!-- Users to Send Automatically -->
                <div    v-if="permissions.can('manage_excursion_report')"
                        class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                    <span>Users to Send to Automatically</span>
                    <select2
                        :multiple="true"
                        :options="users"
                        v-model="selectedUsers"
                    ></select2>
                </div>

                <div class="flex justify-center">
                    <button v-if="isSaving"
                            class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white rounded opacity-50 cursor-not-allowed">
                        <span class="lnr icon-loading animate-spin mr-3"></span>
                        Saving Settings, Please Wait
                    </button>
                    <button v-else-if="permissions.can('manage_excursion_report')"
                            class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white hover:bg-blue-600 hover:text-white
                            focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-600"
                            @click="saveSettings">
                        Save Settings
                    </button>
                </div>
            </template>
        </base-report>
    </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import BaseReport from "./BaseReport"
import Select2 from "@/components/elements/select2"
import Toggle from "@/components/elements/toggle"
import DateTimePicker from "@/components/Reports/VueDatePicker";

import axios from "axios";
import NotificationModal from "@/components/elements/NotificationModal";

export default {
    name: "DetailedAlarmReport",
    mixins: [SonicuCoreMixin],
    components: {NotificationModal, DateTimePicker, BaseReport, Select2, Toggle},
    props: {
        users: Object,
        selectedUsersInput: Array,
        meters: Object,
        intervals: Object,
        intervalInput: String,
        weekdayOptions: Object,
        receiveWeekdayInput: String,
        minDate: Date,
        maxDate: Date,
        logTime1Input: Date,
        logTime2Input: Date,
        styleOptions: Object,
        styleInput: String,
        reportQueued: {
            type: String,
            default: null,
        },
        includeDewPointInput: Boolean,
        includeMktInput: Boolean,
        includeDataInput: Boolean,
        includeTimeOutOfRangeInput: Boolean,
        includeMinMaxInput: Boolean,
    },
    data() {
        return {
            month: this.maxDate,
            format: _.indexOf(['monthly', 'semi-monthly'], this.intervalInput) > -1 ? 'MMMM YYYY' : 'MMMM DD, YYYY',
            viewMode: _.indexOf(['monthly', 'semi-monthly'], this.intervalInput) > -1  ? 'months' : 'days',
            isSaving: false,
            isRunning: false,

            selectedUsers: this.selectedUsersInput,
            selectedMeters: [],
            interval: this.intervalInput,
            receiveWeekday: this.receiveWeekdayInput,

            semiMonthIntervals: {
                first: '1st Half (1 - 15)',
                last: '2nd Half (16 - End of Month)',
            },
            semiMonthSection: new Date().getDate() <= 15 ? 'first' : 'last',

            showReportModal: false,
            reportModalError: false,
            modalMessage: '',
            modalTitle: '',
            isErrorModal: false,
        }
    },
    created() {
        this.selectedUsers = this.selectedUsersInput
    },
    mounted() {

    },
    methods: {
        saveSettings() {
            this.isSaving = true
            let users = []
            Object.keys(this.selectedUsers).forEach(key => {
                users.push(this.selectedUsers[key].toString())
            })
            let formData = {
                report_interval: this.interval,
                subscribed_users: users,
            }
            axios.post(this.company.buildCompanyRoute('reports/detailed-alarm-report/update'), formData)
                .then(response => {
                    this.modalMessage = 'Successfully saved report settings.'
                    this.modalTitle = 'Report Saved'
                    this.reportModalError = false
                    this.showReportModal = true
                })
                .catch(error => {
                    this.modalMessage = 'Error saving report settings.'
                    this.modalTitle = 'Error Saving'
                    this.reportModalError = true
                    this.showReportModal = true
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        runReport() {
            this.isRunning = true
            let formData = {
                month: this.month.toDateString(),
                selected_meters: this.selectedMeters,
                report_interval: this.interval,
                receive_weekday: 1,
            }
            axios.post(this.company.buildCompanyRoute('reports/detailed-alarm-report'), formData)
                .then(response => {
                    this.modalTitle = 'Report Queued'
                    this.modalMessage = 'You will receive an email when the report is prepared.'
                    this.reportModalError = false
                    this.showReportModal = true
                })
                .catch(error => {
                    this.modalTitle = 'Report Error'
                    this.modalMessage = 'Error running report, please try again later.'
                    this.reportModalError = true
                    this.showReportModal = true
                })
                .finally(() => {
                    this.isRunning = false
                })

        },
        zeropadTime(value, length = 2) {
            if (!(value instanceof String)) {
                value = value.toString()
            }
            while(value.length < length) {
                value = '0' + value
            }
            return value
        },
    },
    watch: {
        selectedMeters(value) {
            let allIndex = value.indexOf('all')
            if (allIndex !== -1) {
                let newSelectedMeters = Object.keys(this.meters)
                let metersAllIndex = newSelectedMeters.indexOf("all")
                newSelectedMeters.splice(metersAllIndex, 1)
                this.selectedMeters = newSelectedMeters
            }
        },
        interval(val) {
            if (_.indexOf(['weekly', 'daily'], val) > -1) {
                this.format = 'MMMM DD, YYYY'
                this.viewMode = 'days'
            }
            else if (_.indexOf(['monthly', 'semi-monthly'], val) > -1) {
                this.format = 'MMMM YYYY'
                this.viewMode = 'months'
            }
            this.semiMonthSection = this.month.getDate() <= 15 ? 'first' : 'last'
        },
        semiMonthSection(val) {
            if (val === 'first') {
                this.month.setDate(1)
            }
            else {
                this.month.setDate(16)
            }
        },
        month(val, old) {
            if (_.indexOf(['semi-monthly'], this.interval) > -1) {
                this.month.setDate(old.getDate())
            }

        }
    },
    computed: {
    },
}
</script>

<style scoped>

</style>
